import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Grid,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload,
  Apartment as PropertiesIcon,
  Delete,
  Save
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { auth, storage } from '../components/Security/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signInWithEmailAndPassword } from "firebase/auth";
import Swal from 'sweetalert2';
import { hideLoading, showLoading } from '../redux/slices/loadingSlice';
import { updateProperty, getPropertyById, createPropertyImage, createPropertyTrace, getImagesByPropertyId } from '../actions/PropertyAction';
import { useNavigate, useParams } from 'react-router-dom';
import useStyles from '../theme/useStyles';
import { getTracesByPropertyId } from '../actions/TraceAction';
import { useSelector } from 'react-redux';

const PropertyEditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const { isLoggedIn } = useSelector((state) => state.session);
  const [authVerified, setAuthVerified] = useState(false);
  const [showUnauthorized, setShowUnauthorized] = useState(false);

  // 1. Efecto PRINCIPAL para verificación de autenticación
  useEffect(() => {
    if (isLoggedIn === false && !authVerified) {
      setShowUnauthorized(true);
      setAuthVerified(true);
    } else if (isLoggedIn === true) {
      setAuthVerified(true);
    }
  }, [isLoggedIn, authVerified]);

  // 2. Efecto para mostrar el Swal UNA vez confirmado no-autenticado
  useEffect(() => {
    if (showUnauthorized) {
      Swal.fire({
        title: 'Acceso restringido',
        text: 'Debes iniciar sesión para editar una propiedad',
        icon: 'warning',
        confirmButtonText: 'Ir a login',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login', { state: { from: `/properties/edit/${id}` } });
        } else {
          navigate('/');
        }
      });
    }
  }, [showUnauthorized, navigate, id]);

  const [property, setProperty] = useState({
    name: '',
    address: '',
    price: '',
    codeInternal: '',
    year: new Date().getFullYear(),
    ownerId: ''
  });

  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [traces, setTraces] = useState([{
    propertyId: '',
    dateSale: new Date(),
    name: '',
    value: '',
    tax: 0
  }]);
  const [existingTraces, setExistingTraces] = useState([]);

  // 3. Efecto para cargar datos SOLO cuando está autenticado
  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchData = async () => {
      try {
        showLoading('Loading property data...');
        
        // Fetch owners
        const ownersResponse = await axios.get('/api/owners');
        setOwners(ownersResponse.data);
        
        // Fetch property data
        const propertyResponse = await getPropertyById(id);
        const propertyData = propertyResponse.data;
        
        setProperty({
            name: propertyData.name,
            address: propertyData.address,
            price: propertyData.price,
            codeInternal: propertyData.codeInternal,
            year: propertyData.year,
            ownerId: propertyData.ownerId
        });
        
        // Fetch property images
        const propertyImagesResponse = await getImagesByPropertyId(id);
        console.log('propertyImagesResponse: ',propertyImagesResponse.data);
        if (propertyImagesResponse.data) {
            console.log('propertyImagesResponse dentro de if: ',propertyImagesResponse.data);
            setExistingImages(propertyImagesResponse.data.file);
            setPreviewImage(propertyImagesResponse.data.file); // Show first image as preview
        }
        
        // Fetch property traces
        const propertyTracesResponse = await getTracesByPropertyId(id);
        console.log('propertyTracesResponse: ',propertyTracesResponse);
        if (propertyTracesResponse.data && propertyTracesResponse.data.length > 0) {
            setExistingTraces(propertyTracesResponse.data);
            setTraces(propertyTracesResponse.data.map(trace => ({
                id: trace.id,
                propertyId: trace.propertyId,
                dateSale: new Date(trace.dateSale),
                name: trace.name,
                value: trace.value,
                tax: trace.tax || 0
            })));
            console.log('traces: ', traces);
        }
        
        hideLoading();
      } catch (error) {
        hideLoading();
        console.error('Error fetching data:', error);
        Swal.fire('Error', 'Failed to load property data', 'error');
        navigate('/properties');
      }
    };
    
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProperty(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTraceChange = (index, field, value) => {
    const updatedTraces = [...traces];
    updatedTraces[index][field] = value;
    setTraces(updatedTraces);
  };

  const removeTrace = (index) => {
    const traceToRemove = traces[index];
    
    // If it's an existing trace, we'll need to mark it for deletion
    if (traceToRemove.id) {
      if (window.confirm('Are you sure you want to delete this trace?')) {
        const updatedTraces = [...traces];
        updatedTraces.splice(index, 1);
        setTraces(updatedTraces);
      }
    } else {
      // If it's a new trace, just remove it
      const updatedTraces = [...traces];
      updatedTraces.splice(index, 1);
      setTraces(updatedTraces);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 1. Update property
      showLoading('Updating property...');
      const updatedProperty = {
        ...property,
        price: parseFloat(property.price),
        year: parseInt(property.year)
      };

      const propertyResponse = await updateProperty(id, updatedProperty);
      
      console.log ('status devuelto al form: ', propertyResponse.status);
      
      if (propertyResponse.status !== 204) {
        throw new Error('Failed to update property');
      }

      // 2. Handle image updates
      if (selectedFile) {
        showLoading('Updating image...');
        const imageUrl = await uploadImage();
        
        if (imageUrl) {
          // If there's an existing image, update it, otherwise create new
          if (existingImages.length > 0) {
            // await updatePropertyImage({
            //   id: existingImages[0].id,
            //   id,
            //   file: imageUrl,
            //   enabled: true
            // });
          } else {
            await createPropertyImage({
              id,
              file: imageUrl,
              enabled: true
            });
          }
        }
      }

      // 3. Handle traces
      await handleTracesUpdate();

      Swal.fire('Success', 'Property updated successfully', 'success');
    //   navigate(`/properties/${id}`);
      navigate('/');
    } catch (error) {
      console.error('Error updating property:', error);
      Swal.fire('Error', 'Failed to update property', 'error');
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  const handleTracesUpdate = async () => {
    showLoading('Updating traces...');
    
    try {
      // Process existing traces (update or delete)
      for (const existingTrace of existingTraces) {
        const traceInForm = traces.find(t => t.id === existingTrace.id);
        
        if (traceInForm) {
          // Update existing trace
        //   await updatePropertyTrace({
        //     id: existingTrace.id,
        //     propertyId,
        //     dateSale: traceInForm.date,
        //     name: traceInForm.name,
        //     value: traceInForm.value,
        //     tax: traceInForm.tax || 0
        //   });
        } else {
          // Trace was removed - delete it
        //   await deletePropertyTrace(existingTrace.id);
        }
      }
      
      // Add new traces
      const newTraces = traces.filter(t => !t.id);
      for (const newTrace of newTraces) {
        await createPropertyTrace({
          id,
          dateSale: newTrace.date,
          name: newTrace.name,
          value: newTrace.value,
          tax: newTrace.tax || 0
        });
      }
      
    } catch (error) {
      console.error('Error updating traces:', error);
      throw error;
    } finally {
      hideLoading();
    }
  };

  const calculateTotal = (value, tax) => {
    const numericValue = parseFloat(value) || 0;
    const numericTax = parseFloat(tax) || 0;
    const taxAmount = numericValue * (numericTax / 100);
    return (numericValue + taxAmount).toFixed(2);
  };

  const uploadImage = async () => {
    if (!selectedFile) return null;

    try {
      // 1. Autenticar al usuario con credenciales del .env
      const userCredential = await signInWithEmailAndPassword(
        auth,
        process.env.REACT_APP_FIREBASE_AUTH_EMAIL,
        process.env.REACT_APP_FIREBASE_AUTH_PASSWORD
      );

      console.log("Usuario autenticado:", userCredential.user.uid);

      const storageRef = ref(storage, `property-images/${Date.now()}-${selectedFile.name}`);
      const snapshot = await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      // return await getDownloadURL(snapshot.ref);

      // 3. Cerrar sesión (opcional)
      await auth.signOut();

      console.log('downloadURL: ', downloadURL);

      return downloadURL;

    } catch (error) {
      console.error('Error uploading image:', error);
      Swal.fire('Error', 'No se pudo subir la imagen', 'error');
      return null;
    }
  };
  
  const uploadImageProperty = async (propertyId) => {
    showLoading('Uploading image...');
    if (selectedFile) {
      try {
        const imageUrl = await uploadImage();
        console.log('image url: ', imageUrl);
        if (imageUrl) {
          const newPropertyImage = {
            id: '0',
            propertyId: propertyId,
            file: imageUrl,
            enabled: true
          }

          console.log('request propertImage: ', newPropertyImage);
          const response = await createPropertyImage(newPropertyImage);
          console.log('response image: ', response);

          hideLoading();
          if(response.status === 200){
            Swal.fire({
              title: 'Property image created!',
              text: "ID: " + response.data.id + " - " + response.data.name,
              icon: 'success',
            });
            return { imageUrl };
          }
          else{
            Swal.fire({
              title: 'Errors creating property image',
              text: propertyId,
              icon: 'warning'
            });
            return { imageUrl };
          }
        }
        Swal.fire({
          title: 'Errors creating property image',
          text: 'No se ha podido subir la imagen al repositorio',
          icon: 'warning'
        });
        return null;
      } catch (error) {
        hideLoading();
        console.error('Error uploading image:', error);
        console.error('Unexpected error creating propertyImage:', error);
        Swal.fire({
            title: 'Unexpected error!',
            text: 'Please try again later',
            icon: 'error'
        });
        return null;
      }
    }
  };

  const handleUpdateImage = async () => {
    if (!selectedFile || !id) return;

    try {
      let imageUrl = null;
      if (selectedFile) {
        imageUrl = await uploadImageProperty(id);
        console.log('imagen en repositorio: ', imageUrl)
      }
      
    } catch (error) {
      console.error('Error al actualizar la imagen:', error);
    }
  };

  // Render condicional
  if (!authVerified) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return null; // El Swal ya se está mostrando
  }
  
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      minHeight: '100vh',
      p: 2,
      boxSizing: 'border-box'
    }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card className={classes.card}
          sx={{ 
            width: '100%',
            maxWidth: 1200,
            margin: 'auto',
            mt: 4,
            p: 2
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <CardHeader 
              classes={{ title: classes.cardTitle }}
              avatar={
                <PropertiesIcon style={{fontsize: "large", color: "#494949",}} />
              }
              title="Actualizar Propiedad" 
              subheader="Digite datos a actualizar"
              action={
                <Grid container spacing={1} alignItems="center" justifyContent="center">
                  <Grid item>
                    <Tooltip title="Save" arrow classes={{ tooltip: classes.tooltip }}>
                        <IconButton
                          type="submit"
                          variant="contained"
                          color="primary"
                          size="small"
                          className={classes.button}
                          disabled={loading}
                        >
                            <SaveIcon />
                        </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="Cancel" arrow classes={{ tooltip: classes.tooltip }}>
                      <IconButton
                          variant="contained"
                          color="secondary"
                          onClick={() => navigate('/')}
                          size="small"
                          className={classes.button}
                          disabled={loading}
                      >
                          <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              }
              sx={{ 
                '& .MuiCardHeader-title': { fontSize: '1.25rem' },
                pr: 6
              }}
            />
            
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              {/* información de la propiedad */}
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Información de la Propiedad
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                  <TextField
                    fullWidth
                    size="small"
                    label="Nombre"
                    name="name"
                    value={property.name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6, md: 2 }} >
                  <TextField
                    fullWidth
                    size="small"
                    label="Código Interno"
                    name="codeInternal"
                    value={property.codeInternal}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6, md: 2 }} >
                  <TextField
                    fullWidth
                    size="small"
                    label="Precio"
                    name="price"
                    type="number"
                    value={property.price}
                    onChange={handleChange}
                    required
                    inputProps={{ step: "0.01" }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6, md: 2 }} >
                  <TextField
                    fullWidth
                    size="small"
                    label="Año de construcción"
                    name="year"
                    type="number"
                    value={property.year}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                  {/* <FormControl fullWidth size="small" required sx={{ minWidth: 247 }}> */}
                    <FormControl fullWidth size="small" required sx={{ minWidth: '100%' }}>
                        <InputLabel id="owner-select-label">Propietario</InputLabel>
                        <Select
                        labelId="owner-select-label"
                        name="ownerId"
                        value={property.ownerId}
                        onChange={handleChange}
                        label="Propietario"
                        sx={{ 
                            textAlign: 'left',
                            '& .MuiSelect-select': {
                            width: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                            }
                        }}
                        >
                            {owners.map(owner => (
                                <MenuItem 
                                key={owner.id} 
                                value={owner.id}
                                sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                }}
                                >
                                {owner.name}
                                </MenuItem>
                            ))}
                        </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              {/* Dirección (linea completa) */}
              <Grid container sx={{ mt: 2 }}>
                {/* <Grid item xs={12} sx={{ width: '100%', px: 0 }}>  */}
                <Grid size={{ xs: 12, sm: 12, md: 12 }} >
                  <TextField
                    fullWidth
                    size="small"
                    label="Dirección"
                    name="address"
                    value={property.address}
                    onChange={handleChange}
                    required
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Sección imágenes */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  Imágenes de la Propiedad
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      fullWidth
                    >
                      Seleccionar Imagen
                      <input
                        type="file"
                        hidden
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                    </Button>

                    {/* Botón para actualizar la imagen */}
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleUpdateImage}
                      disabled={!selectedFile} // Deshabilitado si no hay archivo seleccionado
                      startIcon={<Save />}
                    >
                      Actualizar Imagen
                    </Button>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ 
                        height: 120,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        backgroundColor: 'action.hover'
                    }}>
                      {previewImage ? (
                        <img 
                            src={previewImage} 
                            alt="Preview" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                            Vista previa de imagen
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  
                  {selectedFile && (
                    <Grid item xs={12}>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Archivo seleccionado: {selectedFile.name}
                        </Typography>
                    </Grid>
                  )}
                  
                  {existingImages && !selectedFile && (
                    <Grid item xs={12}>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            {/* Current image: {existingImages.file.split('/').pop()} */}
                            Imágen actual: {existingImages}
                        </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Historial de ventas */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Historial de Ventas
                  </Typography>
                  {/* <Button 
                    onClick={addTrace} 
                    startIcon={<Add />}
                    size="small"
                  >
                    Add Record
                  </Button> */}
                </Box>
                
                {traces.map((trace, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                        {/* Fecha */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }} >
                            <DatePicker
                            label="Fecha"
                            value={trace.dateSale}
                            onChange={(newValue) => handleTraceChange(index, 'date', newValue)}
                            slotProps={{ textField: { size: 'small', fullWidth: true, disabled: true } }}
                            />
                        </Grid>
                      
                        {/* Nombre */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                            <TextField
                            fullWidth
                            size="small"
                            label="Nombre"
                            value={trace.name}
                            onChange={(e) => handleTraceChange(index, 'name', e.target.value)}
                            slotProps={{
                              input: {
                                readOnly: true,
                              }
                            }}
                            />
                        </Grid>
                      
                        {/* Valor */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }} >
                            <TextField
                            fullWidth
                            size="small"
                            label="Valor"
                            type="number"
                            value={trace.value}
                            onChange={(e) => handleTraceChange(index, 'value', e.target.value)}
                            inputProps={{ step: "0.01" }}
                            slotProps={{
                              input: {
                                readOnly: true,
                              }
                            }}
                            />
                        </Grid>
                      
                        {/* Tax  */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }} >
                            <TextField
                            fullWidth
                            size="small"
                            label="Tax %"
                            type="number"
                            value={trace.tax || ''}
                            onChange={(e) => handleTraceChange(index, 'tax', e.target.value)}
                            // InputProps={{
                            //     endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            // }}
                            inputProps={{ 
                                step: "0.1",
                                min: "0",
                                max: "100"
                            }}
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                              endAdornment: {
                                position: 'end',
                                children: '%',
                              }
                            }}
                            />
                        </Grid>
                      
                        {/* Total (Calculado) */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }} >
                            <TextField
                            fullWidth
                            size="small"
                            label="Total"
                            value={calculateTotal(trace.value, trace.tax)}
                            // InputProps={{
                            //     readOnly: true,
                            //     startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            // }}
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                              startAdornment: {
                                position: 'start',
                                children: '$',
                              }
                            }}
                            />
                        </Grid>
                      
                        {/* Actions */}
                        <Grid size={{ xs: 12, sm: 6, md: 1 }} >
                            <Button 
                            onClick={() => removeTrace(index)}
                            color="error"
                            size="small"
                            fullWidth
                            >
                            <Delete fontSize="small" />
                            </Button>
                        </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Box>
        </Card>
      </LocalizationProvider>
    </Box>
  );
};

export default PropertyEditForm;